import { useAtom } from "jotai";
import { startTransition, useCallback, useRef } from "react";
import type { FunctionComponent, ReactNode } from "react";
import { Transition } from "react-transition-group";

import { useEditionValue } from "shared/lib/edition";
import { yieldToMain } from "shared/lib/utils";

import { hamburgerMenuAtom } from "scmp-app/components/hamburger-menu/atoms";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";

import type { ContainerProps } from "./styles";
import { Container, Layer, MenuWrapper } from "./styles";

export type Props = {
  anchor?: ContainerProps["$anchor"];
  backdrop?: boolean;
  children?: (props: ChildrenProps) => ReactNode;
};

type ChildrenProps = {
  handleClose: () => void;
  handleCloseExplicit: () => void;
};

export const HamburgerMenuWrapper: FunctionComponent<Props> = ({
  anchor = "right",
  backdrop,
  children,
}) => {
  const [hamburgerMenu, setHamburgerMenuState] = useAtom(hamburgerMenuAtom);
  const nodeReference = useRef(null);
  const edition = useEditionValue();

  // handle close menu like navigation
  const handleClose = useCallback(() => {
    startTransition(() => {
      setHamburgerMenuState(current => ({ ...current, isOpen: false }));
    });
  }, [setHamburgerMenuState]);

  // user explicitly close the menu
  // trigger GA4 tracking
  const handleCloseExplicit = useCallback(() => {
    void (async () => {
      await yieldToMain({ priority: "user-blocking" });
      handleClose();
    })();

    void (async () => {
      await yieldToMain({ priority: "background" });
      sendGA4Tracking({
        action: "click",
        category: "menu",
        customized_parameters: {
          action_type: "close",
          edition,
          menu_type: "hamburger",
        },
        subcategory: "section",
      });
    })();
  }, [edition, handleClose]);

  return (
    <Transition appear in={hamburgerMenu.isOpen} nodeRef={nodeReference} timeout={200}>
      {state => (
        <Container $anchor={anchor} $backdrop={backdrop} $state={state} ref={nodeReference}>
          <Layer onClick={handleCloseExplicit} />
          <MenuWrapper>{children && children({ handleClose, handleCloseExplicit })}</MenuWrapper>
        </Container>
      )}
    </Transition>
  );
};
